import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Form, Pagination } from 'react-bootstrap';
import clinicalReportsService from '../../../services/clinicalReportsService';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import ReportSectionHead from './ReportSectionHead';
import { HeadDoctorStatCard } from '../ui';

const COPY = HEAD_DOCTOR_COPY.REPORTS;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 150, 200];

const DEFAULT_AGE_BUCKETS = [
  { key: '0_1m', label: '0 à 1 mois', short: '0–1m' },
  { key: '1_11m', label: '1 à 11 mois', short: '1–11m' },
  { key: '1_2y', label: '1 à 2 ans', short: '1–2a' },
  { key: '2_4y', label: '2 à 4 ans', short: '2–4a' },
  { key: '5_14y', label: '5 à 14 ans', short: '5–14a' },
  { key: '15_24y', label: '15 à 24 ans', short: '15–24a' },
  { key: '25p', label: '25 ans et +', short: '25a+' }
];

function formatCell(value) {
  const n = Number(value || 0);
  return n > 0 ? n.toLocaleString('fr-FR') : '—';
}

function SexAgeGrid({ title, tone, buckets, values }) {
  return (
    <div className={`hd-morbidity-card__sex hd-morbidity-card__sex--${tone}`}>
      <h4 className="hd-morbidity-card__sex-title">{title}</h4>
      <div className="hd-morbidity-card__ages">
        {buckets.map((bucket) => (
          <div key={bucket.key} className="hd-morbidity-card__age">
            <span className="hd-morbidity-card__age-label">{bucket.short || bucket.label}</span>
            <strong className="hd-morbidity-card__age-value">
              {formatCell(values?.[bucket.key])}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HeadDoctorReportsMorbiditePage() {
  const { reportFilters } = useClinicalReports();
  const [rows, setRows] = useState([]);
  const [ageBuckets, setAgeBuckets] = useState(DEFAULT_AGE_BUCKETS);
  const [summary, setSummary] = useState({ diseases: 0, cases: 0, hospitalizations: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(150);

  const load = useCallback(async () => {
    if (!reportFilters) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await clinicalReportsService.getMorbidity(reportFilters);
      if (res.success) {
        const apiBuckets = res.data?.age_buckets || [];
        const mergedBuckets = (apiBuckets.length ? apiBuckets : DEFAULT_AGE_BUCKETS).map((bucket) => {
          const fallback = DEFAULT_AGE_BUCKETS.find((b) => b.key === bucket.key);
          return {
            ...fallback,
            ...bucket,
            short: bucket.short || fallback?.short || bucket.label
          };
        });
        setRows(res.data?.rows || []);
        setAgeBuckets(mergedBuckets);
        setSummary(res.data?.summary || { diseases: 0, cases: 0, hospitalizations: 0 });
        setPage(1);
      } else {
        setRows([]);
        setSummary({ diseases: 0, cases: 0, hospitalizations: 0 });
        setError(res.message || COPY.ERROR_MORBIDITY_LOAD);
      }
    } catch {
      setRows([]);
      setError(COPY.ERROR_MORBIDITY_LOAD);
    } finally {
      setLoading(false);
    }
  }, [reportFilters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const safePage = Math.min(Math.max(page, 1), totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  const empty = !loading && rows.length === 0;
  const hasPrev = safePage > 1;
  const hasNext = safePage < totalPages;

  return (
    <div className="hd-morbidity-page">
      <ReportSectionHead
        badge={COPY.SUB_MORBIDITE}
        title={COPY.PAGE_MORBIDITE_TITLE}
        description={COPY.PAGE_MORBIDITE_DESC}
      />

      <Row className="g-2 mb-3">
        <Col xs={12} sm={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_MORBIDITE_DISEASES}
            value={summary.diseases}
            icon="alertCircle"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_MORBIDITE_CASES}
            value={summary.cases}
            icon="stethoscope"
            tone="indigo"
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_MORBIDITE_HOSP}
            value={summary.hospitalizations}
            icon="building"
            tone="teal"
            loading={loading}
            subtitle={COPY.KPI_MORBIDITE_HOSP_HINT}
          />
        </Col>
      </Row>

      {error ? <p className="text-danger small mb-2">{error}</p> : null}

      <div className="hd-morbidity-toolbar">
        <Form.Group className="hd-morbidity-toolbar__pagesize mb-0">
          <Form.Label className="mb-0">{COPY.MORBIDITE_PER_PAGE}</Form.Label>
          <Form.Select
            size="sm"
            value={String(pageSize)}
            disabled={loading || rows.length === 0}
            onChange={(e) => setPageSize(Number(e.target.value) || 150)}
            aria-label={COPY.MORBIDITE_PER_PAGE}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <span className="hd-morbidity-toolbar__status">
          {COPY.MORBIDITE_PAGE_STATUS(safePage, totalPages, rows.length)}
        </span>
      </div>

      <div className="hd-morbidity-legend" aria-hidden>
        <span className="hd-morbidity-legend__item hd-morbidity-legend__item--m">
          {COPY.MORBIDITE_GROUP_M}
        </span>
        <span className="hd-morbidity-legend__item hd-morbidity-legend__item--f">
          {COPY.MORBIDITE_GROUP_F}
        </span>
      </div>

      {loading ? (
        <div className="hd-morbidity-empty">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
          {COPY.LOADING}
        </div>
      ) : empty ? (
        <div className="hd-morbidity-empty">{COPY.EMPTY_MORBIDITE}</div>
      ) : (
        <div className="hd-morbidity-cards">
          {pagedRows.map((row) => (
            <article key={row.disease_id} className="hd-morbidity-card">
              <header className="hd-morbidity-card__head">
                <code className="hd-morbidity-card__code">{row.disease_code || '—'}</code>
                <h3 className="hd-morbidity-card__title">{row.disease_name}</h3>
                <div className="hd-morbidity-card__meta">
                  <span>
                    {COPY.MORBIDITE_COL_TOTAL}{' '}
                    <strong>{Number(row.total || 0).toLocaleString('fr-FR')}</strong>
                  </span>
                  <span>
                    {COPY.MORBIDITE_COL_HOSP_SHORT}{' '}
                    <strong>{formatCell(row.hospitalizations)}</strong>
                  </span>
                </div>
              </header>
              <div className="hd-morbidity-card__body">
                <SexAgeGrid
                  title={COPY.MORBIDITE_GROUP_M}
                  tone="m"
                  buckets={ageBuckets}
                  values={row.masculin}
                />
                <SexAgeGrid
                  title={COPY.MORBIDITE_GROUP_F}
                  tone="f"
                  buckets={ageBuckets}
                  values={row.feminin}
                />
              </div>
            </article>
          ))}
        </div>
      )}

      {!empty ? (
        <div className="hd-morbidity-pagination">
          <Pagination size="sm" className="mb-0 flex-wrap justify-content-center justify-content-md-end">
            <Pagination.First onClick={() => setPage(1)} disabled={!hasPrev || loading} />
            <Pagination.Prev
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!hasPrev || loading}
            />
            <Pagination.Item active>{safePage}</Pagination.Item>
            <Pagination.Next
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={!hasNext || loading}
            />
            <Pagination.Last onClick={() => setPage(totalPages)} disabled={!hasNext || loading} />
          </Pagination>
        </div>
      ) : null}

      <p className="hd-morbidity-footnote mb-0">{COPY.MORBIDITE_FOOTNOTE}</p>
    </div>
  );
}
